import React from "react";
import { motion } from "framer-motion";
import {
  FiCheck,
  FiCheckCircle,
  FiAlertTriangle,
  FiFile,
  FiDownload,
  FiPlay,
  FiClock,
} from "react-icons/fi";

// Animation variants
const messageVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15 },
  },
};

// Format timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Get avatar gradient
const getAvatarGradient = (name) => {
  if (!name) return "grad-1";
  const code = name.charCodeAt(0) % 5;
  return `grad-${code + 1}`;
};

// Message status icon - Enhanced for all states
const MessageStatus = ({ status, isTemporary }) => {
  // Handle string status (from optimistic updates)
  const statusStr = typeof status === "string" ? status : null;
  const statusObj = typeof status === "object" ? status : null;

  // Sending state (temporary message or status="sending")
  if (isTemporary || statusStr === "sending" || statusObj?.sending) {
    return (
      <FiClock className="msgx-message-status sending" title="Sending..." />
    );
  }

  // Failed state
  if (statusStr === "failed" || statusObj?.failed || statusObj?.error) {
    return (
      <FiAlertTriangle
        className="msgx-message-status error"
        title={statusObj?.error || "Failed to send"}
      />
    );
  }

  // Read state (double check, blue)
  if (statusObj?.read) {
    return (
      <span className="msgx-message-status-double read" title="Read">
        <FiCheck />
        <FiCheck style={{ marginLeft: -6 }} />
      </span>
    );
  }

  // Delivered state (double check, gray)
  if (statusObj?.delivered) {
    return (
      <span className="msgx-message-status-double" title="Delivered">
        <FiCheck />
        <FiCheck style={{ marginLeft: -6 }} />
      </span>
    );
  }

  // Sent state (single check) - default for string "sent" or object with sent:true
  if (statusStr === "sent" || statusObj?.sent !== false) {
    return <FiCheck className="msgx-message-status" title="Sent" />;
  }

  return <FiClock className="msgx-message-status sending" title="Sending..." />;
};

const MessageBubble = ({
  message,
  isMine,
  showAvatar,
  otherParticipant,
  currentUserId,
  onReply,
  onReact,
  editingMessageId,
  editContent,
  setEditContent,
  onCancelEdit,
  onSubmitEdit,
}) => {
  const isDeleted = message.isDeleted || !message.content;

  return (
    <motion.div
      className={`msgx-message-wrapper ${isMine ? "sent" : "received"}`}
      variants={messageVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      layout
      onDoubleClick={() => !isDeleted && onReply(message)}
    >
      {/* Avatar for received messages */}
      {!isMine && showAvatar && (
        <>
          {otherParticipant?.profilePhotoUrl ? (
            <img
              src={otherParticipant.profilePhotoUrl}
              alt={otherParticipant?.name}
              className="msgx-message-avatar"
            />
          ) : (
            <div
              className={`msgx-message-avatar-initials ${getAvatarGradient(
                otherParticipant?.name
              )}`}
            >
              {otherParticipant?.name?.charAt(0).toUpperCase() || "?"}
            </div>
          )}
        </>
      )}

      {/* Message Content */}
      <div className="msgx-message-content-wrapper">
        {/* Reply Preview */}
        {message.replyTo && (
          <div className={`msgx-reply-preview ${isMine ? "sent" : "received"}`}>
            <p className="msgx-reply-sender">
              {message.replyTo.sender?._id === currentUserId
                ? "You"
                : message.replyTo.sender?.name || "Unknown"}
            </p>
            <p className="msgx-reply-content-text">
              {message.replyTo.isDeleted || !message.replyTo.content
                ? "🚫 Message was deleted"
                : message.replyTo.content}
            </p>
          </div>
        )}

        {/* Bubble */}
        <div className={`msgx-message-bubble ${isMine ? "sent" : "received"}`}>
          {/* File Message */}
          {message.type === "file" && (
            <div className="msgx-file-message">
              <FiFile className="msgx-file-icon" />
              <div className="msgx-file-info">
                <span className="msgx-file-name">{message.content}</span>
                <span className="msgx-file-size">{message.fileSize}</span>
              </div>
              <FiDownload className="msgx-file-download" />
            </div>
          )}

          {/* Image Message */}
          {message.type === "image" && (
            <div className="msgx-image-message">
              <img
                src={
                  message.imageUrl ||
                  `https://source.unsplash.com/random/400x300/?chart&${message._id}`
                }
                alt={message.content || "Shared image"}
                loading="lazy"
              />
            </div>
          )}

          {/* Audio Message */}
          {message.type === "audio" && (
            <div className="msgx-audio-message">
              <button className="msgx-audio-play-btn">
                <FiPlay />
              </button>
              <div className="msgx-audio-waveform">
                {Array(12)
                  .fill()
                  .map((_, i) => (
                    <div
                      key={i}
                      className="msgx-waveform-bar"
                      style={{ height: `${Math.random() * 16 + 6}px` }}
                    />
                  ))}
              </div>
              <span className="msgx-audio-duration">0:12</span>
            </div>
          )}

          {/* Text Message */}
          {message.type === "text" && (
            <>
              {editingMessageId === message._id ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    onSubmitEdit(message._id);
                  }}
                  className="msgx-edit-form"
                >
                  <input
                    type="text"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    onKeyDown={(e) => e.key === "Escape" && onCancelEdit()}
                    autoFocus
                    className="msgx-input-field"
                    style={{ marginBottom: 8 }}
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: "flex-end",
                    }}
                  >
                    <button type="submit" className="msgx-btn msgx-btn-primary">
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={onCancelEdit}
                      className="msgx-btn msgx-btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <p className="msgx-message-text">
                  {isDeleted ? (
                    <span className="msgx-message-deleted">
                      🚫 This message was deleted
                    </span>
                  ) : (
                    <>
                      {message.content}
                      {message.isEdited && (
                        <span className="msgx-message-edited">(edited)</span>
                      )}
                    </>
                  )}
                </p>
              )}
            </>
          )}

          {/* Reactions */}
          {!isDeleted && message.reactions && message.reactions.length > 0 && (
            <div className="msgx-reactions">
              {Object.entries(
                message.reactions.reduce((acc, r) => {
                  acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                  return acc;
                }, {})
              ).map(([emoji, count]) => (
                <motion.button
                  key={emoji}
                  className={`msgx-reaction-chip ${
                    isMine ? "sent" : "received"
                  }`}
                  onClick={() => onReact(message._id, emoji)}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>{emoji}</span>
                  <span className="msgx-reaction-count">{count}</span>
                </motion.button>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="msgx-message-footer">
            <span className="msgx-message-time">
              {formatTimestamp(message.createdAt)}
            </span>
            {isMine && (
              <MessageStatus
                status={message.status}
                isTemporary={message.isTemporary || message.isTemp}
              />
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MessageBubble;
